import { Box, Link as MuiLink, Skeleton, Typography } from '@mui/material';
import { RaRecord, useGetList } from 'react-admin';
import { Link } from 'react-router-dom';
import { UUID_RE } from './urlParams';

interface EntityLookupProps {
  label: string;
  resource: 'companies' | 'profiles';
  uuid: string;
  nameField: string;
}

/**
 * Looks up one record by its uuid — the companies/profiles resources are keyed by a numeric `id`
 * for their Show route, not the uuid the recalculate form takes — and renders its name as plain
 * text with a link to that Show page. Says nothing while the uuid is incomplete/invalid: this is a
 * convenience label next to the uuid fields, not a form control with its own error state.
 */
const EntityLookup = ({ label, resource, uuid, nameField }: EntityLookupProps) => {
  const valid = UUID_RE.test(uuid);
  const { data, isLoading } = useGetList<RaRecord>(resource, { filter: { uuid }, pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' } }, { enabled: valid });

  if (!valid) return null;

  if (isLoading) {
    return (
      <Typography variant="body2" color="text.secondary">
        {label}: <Skeleton component="span" width={100} sx={{ display: 'inline-block' }} />
      </Typography>
    );
  }

  const record = data?.[0];
  if (!record) {
    return (
      <Typography variant="body2" color="text.secondary">
        {label}: not found
      </Typography>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      {label}:{' '}
      <MuiLink component={Link} to={`/${resource}/${record.id}/show`}>
        {String(record[nameField])}
      </MuiLink>
    </Typography>
  );
};

interface EntitySummaryProps {
  companyUuid: string;
  userProfileUuid: string;
}

/** Names the company/employee the uuids above resolve to, each linking to its Show page in this admin. */
export const EntitySummary = ({ companyUuid, userProfileUuid }: EntitySummaryProps) => {
  if (!UUID_RE.test(companyUuid) && !UUID_RE.test(userProfileUuid)) return null;
  return (
    <Box sx={{ display: 'flex', gap: 3, mb: 1.5, flexWrap: 'wrap' }}>
      <EntityLookup label="Company" resource="companies" uuid={companyUuid} nameField="name" />
      <EntityLookup label="Employee" resource="profiles" uuid={userProfileUuid} nameField="fullName" />
    </Box>
  );
};

export default EntitySummary;
